/**
 * Ficha da paciente → Plano alimentar → "Distribuição de refeições desta paciente".
 * Quantas refeições ela faz, horários e % das kcal em cada uma. Tem prioridade
 * sobre o padrão da nutricionista (Configurações → Distribuição de refeições),
 * que aqui serve só de ponto de partida. A soma precisa fechar 100%.
 */
import React, { useEffect, useState } from 'react';
import { distribuicaoRefeicoesService, pacienteDistribuicaoService } from '../../services/api';
import { mealIcon } from '../PlanoAlimentar/helpers';
import { useToast } from '../../contexts/ToastContext';
import { Ico, emo } from '../ui/Ico';

const BRAND = '#D4A574';
const S = {
  card:  { background: '#ffffff', border: '1.5px solid #ebe3da', borderRadius: 14, padding: '1.25rem', marginBottom: '1rem' },
  input: { border: '1.5px solid #ebe3da', borderRadius: 8, padding: '0.4rem 0.55rem', fontSize: '0.86rem', fontFamily: 'inherit', background: '#ffffff', color: '#2f2a26' },
  btn:   { border: 'none', borderRadius: 8, padding: '0.5rem 0.95rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' },
  chip:  { border: '1px solid #ebe3da', background: '#ffffff', borderRadius: 6, cursor: 'pointer', padding: '0.25rem 0.5rem', fontSize: '0.8rem', color: '#736b64' },
};

const limpa = (r) => ({ chave: r.chave, nome: r.nome, pct_kcal: Number(r.pct_kcal), horario: r.horario || '', perfil: r.perfil || null });

export default function DistribuicaoPaciente({ patientId }) {
  const { showSuccess, showError } = useToast();
  const [info, setInfo]         = useState(null);   // GET do paciente (salva, anamnese, catálogo, limites)
  const [padroes, setPadroes]   = useState([]);     // distribuições padrão (nutri + sistema)
  const [linhas, setLinhas]     = useState([]);
  const [salva, setSalva]       = useState(false);  // linhas == distribuição salva da paciente
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro]         = useState('');

  const lim = info?.limites || { min_refeicoes: 2, max_refeicoes: 8, pct_min: 2, pct_max: 60, faixa_padrao: [3, 7] };

  // Padrão geral para n: o da nutricionista, senão o do sistema
  const padraoPara = (n, lista = padroes) => {
    const pega = (o) => lista.find(d => d.origem === o && d.programa === '' && d.num_refeicoes === n);
    return (pega('minha') || pega('sistema'))?.refeicoes || null;
  };

  useEffect(() => {
    let vivo = true;
    Promise.all([pacienteDistribuicaoService.get(patientId), distribuicaoRefeicoesService.list()])
      .then(([p, d]) => {
        if (!vivo) return;
        if (!p?.success) { setErro(p?.error || 'Não foi possível carregar.'); return; }
        const ps = d?.success ? (d.data.distribuicoes || []) : [];
        setInfo(p.data); setPadroes(ps);
        if (p.data.distribuicao) { setLinhas(p.data.distribuicao.map(limpa)); setSalva(true); return; }
        const [lo, hi] = p.data.limites?.faixa_padrao || [3, 7];
        const nAnam = p.data.anamnese?.num_refeicoes;
        const n = nAnam ? Math.max(lo, Math.min(hi, nAnam)) : 5;
        setLinhas((padraoPara(n, ps) || []).map(limpa)); setSalva(false);
      })
      .catch(e => { if (vivo) setErro(e?.response?.data?.error || 'Não foi possível carregar.'); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const editar = (l) => { setLinhas(l); setSalva(false); };
  const setCampo = (i, campo, v) => editar(linhas.map((r, j) => (j === i ? { ...r, [campo]: v } : r)));
  const remover = (i) => editar(linhas.filter((_, j) => j !== i));
  const adicionar = (chave) => {
    const cat = info?.catalogo?.[chave];
    const usadas = new Set(linhas.map(r => r.chave));
    let k = chave; let n = 1;
    if (!cat) { while (usadas.has(`extra_${n}`)) n++; k = `extra_${n}`; }
    editar([...linhas, { chave: k, nome: cat?.nome || 'Nova refeição', pct_kcal: 5, horario: cat?.horario || '', perfil: cat?.perfil || null }]);
  };
  const aplicarPadrao = (n) => {
    const p = padraoPara(n);
    if (!p) { showError(`Não há distribuição padrão para ${n} refeições.`); return; }
    if (linhas.length && !window.confirm(`Trocar as refeições atuais pelo padrão de ${n} refeições?`)) return;
    editar(p.map(limpa));
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const r = await pacienteDistribuicaoService.save(patientId, linhas.map(limpa));
      if (!r?.success) { showError(r?.error || 'Não foi possível salvar.'); return; }
      setLinhas(r.data.distribuicao.map(limpa)); setSalva(true);
      setInfo(i => ({ ...i, distribuicao: r.data.distribuicao }));
      showSuccess('Distribuição salva — os próximos planos desta paciente partem dela.');
    } catch (e) { showError(e?.response?.data?.error || 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  };
  const apagar = async () => {
    if (!window.confirm('Apagar a distribuição desta paciente? Os próximos planos voltam a partir do seu padrão.')) return;
    try {
      const r = await pacienteDistribuicaoService.remove(patientId);
      if (!r?.success) { showError(r?.error || 'Não foi possível remover.'); return; }
      setInfo(i => ({ ...i, distribuicao: null })); setSalva(false);
      const [lo, hi] = lim.faixa_padrao || [3, 7];
      const nAnam = info?.anamnese?.num_refeicoes;
      setLinhas((padraoPara(nAnam ? Math.max(lo, Math.min(hi, nAnam)) : 5) || []).map(limpa));
      showSuccess('Distribuição da paciente removida.');
    } catch (e) { showError(e?.response?.data?.error || 'Não foi possível remover.'); }
  };

  if (erro) return <div style={S.card}><p style={{ margin: 0, color: '#a8483f', fontSize: '0.85rem' }}><Ico e="⚠" sp />{erro}</p></div>;
  if (!info) return <div style={{ ...S.card, color: '#736b64', fontSize: '0.85rem' }}>Carregando distribuição de refeições…</div>;

  const n = linhas.length;
  const soma = linhas.reduce((t, r) => t + (Number(r.pct_kcal) || 0), 0);
  const somaOk = Math.abs(soma - 100) < 0.05;
  const anam = info.anamnese;
  const disponiveis = Object.entries(info.catalogo || {}).filter(([k]) => !linhas.some(r => r.chave === k));
  const temSalva = !!info.distribuicao;

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: '1rem', color: '#2f2a26' }}><Ico e="🍽" sp />Distribuição de refeições desta paciente</p>
        <span style={{ padding: '0.2rem 0.6rem', borderRadius: 999, fontWeight: 600, fontSize: '0.78rem',
                       background: temSalva ? '#edf4ee' : '#f6f0ea', color: temSalva ? '#2f5236' : '#5a3e2b' }}>
          {temSalva ? (salva ? 'Salva para a paciente' : 'Alterações não salvas') : 'Usando o seu padrão'}
        </span>
      </div>
      <p style={{ margin: '0 0 0.85rem', fontSize: '0.83rem', color: '#736b64' }}>
        {anam?.num_refeicoes
          ? <>Na anamnese: <strong>{anam.num_refeicoes} refeição(ões)/dia</strong>{anam.horarios ? <> — {anam.horarios}</> : null}. </>
          : 'A anamnese não informa quantas refeições ela faz. '}
        Os planos gerados para ela partem desta distribuição.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.82rem', color: '#2f2a26', fontWeight: 600, marginRight: 4 }}>Partir do padrão:</span>
        {[3, 4, 5, 6, 7].map(q => (
          <button key={q} onClick={() => aplicarPadrao(q)} aria-pressed={n === q}
            style={{ ...S.btn, padding: '0.35rem 0.7rem', background: n === q ? BRAND : '#f6f0ea', color: n === q ? '#ffffff' : '#5a3e2b' }}>{q}</button>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', minWidth: 460 }}>
          <thead><tr style={{ textAlign: 'left', color: '#736b64' }}>
            <th scope="col" style={{ padding: 6 }}>Refeição</th>
            <th scope="col" style={{ padding: 6, width: 104 }}>Horário</th>
            <th scope="col" style={{ padding: 6, width: 104 }}>% das kcal</th>
            <th scope="col" style={{ padding: 6, width: 36 }}><span className="sr-only">Remover</span></th>
          </tr></thead>
          <tbody>
            {linhas.map((r, i) => {
              const fora = Number(r.pct_kcal) < lim.pct_min || Number(r.pct_kcal) > lim.pct_max;
              return (
                <tr key={r.chave} style={{ borderTop: '1px solid #ebe3da' }}>
                  <td style={{ padding: 6 }}>
                    <span aria-hidden="true" style={{ marginRight: 4 }}>{emo(mealIcon(r.chave))}</span>
                    <input style={{ ...S.input, width: 'calc(100% - 28px)' }} value={r.nome} maxLength={40}
                      aria-label={`Nome da refeição ${i + 1}`} onChange={e => setCampo(i, 'nome', e.target.value)} />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input type="time" style={{ ...S.input, width: 96 }} value={r.horario || ''} aria-label={`Horário de ${r.nome}`}
                      onChange={e => setCampo(i, 'horario', e.target.value)} />
                  </td>
                  <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                    <input type="number" min={lim.pct_min} max={lim.pct_max} step="1" inputMode="decimal"
                      style={{ ...S.input, width: 64, borderColor: fora ? '#a8483f' : '#ebe3da' }} value={r.pct_kcal}
                      aria-label={`Percentual de calorias de ${r.nome}`} aria-invalid={fora}
                      onChange={e => setCampo(i, 'pct_kcal', e.target.value === '' ? '' : Number(e.target.value))} /> %
                  </td>
                  <td style={{ padding: 6 }}>
                    <button style={{ ...S.chip, color: '#a8483f' }} onClick={() => remover(i)} disabled={n <= lim.min_refeicoes}
                      aria-label={`Remover ${r.nome}`}><Ico e="✕" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {n < lim.max_refeicoes && (
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.6rem', fontSize: '0.82rem' }}>
          <span style={{ color: '#736b64' }}>+ Adicionar:</span>
          {disponiveis.map(([k, c]) => (
            <button key={k} style={S.chip} onClick={() => adicionar(k)}>{emo(mealIcon(k))} {emo(c.nome)}</button>
          ))}
          <button style={S.chip} onClick={() => adicionar('outra')}><Ico e="🍴" sp />Outra…</button>
        </div>
      )}

      <p aria-live="polite" style={{ margin: '0.85rem 0', fontSize: '0.85rem', color: somaOk ? '#2f5236' : '#8a5f1a' }}>
        Soma: <strong>{Math.round(soma * 10) / 10}%</strong>{' '}
        {somaOk ? <Ico e="✓" /> : (soma < 100 ? `— faltam ${Math.round((100 - soma) * 10) / 10}%` : `— sobram ${Math.round((soma - 100) * 10) / 10}%`)}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={salvar} disabled={!somaOk || salvando || !n || salva}
          style={{ ...S.btn, background: somaOk && !salva ? BRAND : '#d9d0c6', color: '#ffffff', cursor: somaOk && !salva ? 'pointer' : 'default' }}>
          {salvando ? 'Salvando…' : <><Ico e="💾" sp />{temSalva ? 'Salvar alterações' : 'Salvar para esta paciente'}</>}
        </button>
        {temSalva && (
          <button onClick={apagar} style={{ ...S.btn, background: '#ffffff', color: '#736b64', border: '1.5px solid #ebe3da' }}>
            ↺ Voltar ao meu padrão
          </button>
        )}
      </div>
    </div>
  );
}
